/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ITableDataModal } from "pages/StepPages/StepFour/One/Tables/models";
import { useParams } from "react-router-dom";
import sass from "./levels.module.sass";
import InputTemp from "components-new/InputTemp";
import TextArea from "components-new/Textarea";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import ModalPortal from "components-new/ModalPortal";
import { snackWrapper } from "helpers";
import { ChangeActiveTabContext } from "../TableNavigate";
import { ThreatImplementationLevelResponse, ViolatorLevelResponse } from "libs/apiClient/data-contracts";
import { directoryAPI } from "libs/apiClient/Directory";
import { organizationAPI } from "libs/apiClient/Organization";

interface ILevelsOfVariablesModule extends Omit<ITableDataModal<ThreatImplementationLevelResponse>, "active"> {
	active?: IOmitLevel | null;
}
interface IOmitLevel extends Omit<ThreatImplementationLevelResponse, "violatorType"> {
	type: string;
}

const initial: IOmitLevel = {
	id: 0,
	level: "",
	type: "",
	possibility: "",
	name: "",
};

const LevelsOfVariablesModule: React.FC<ILevelsOfVariablesModule> = ({ toggle, replace, active, data = [], close }) => {
	const { updateTabs } = useContext(ChangeActiveTabContext);
	const [options, setOptions] = useState<ViolatorLevelResponse[]>([]);
	const [currentObject, setCurrentObject] = useState<IOmitLevel>(initial);

	const params = useParams();

	const changeField = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setCurrentObject((prevState: any) => {
			return { ...prevState, [name]: value };
		});
	};

	const title = useMemo(() => {
		return `${
			toggle === "edit" ? "Редактирование: " : "Создание: "
		}Уровни возможностей нарушителей по реализации угроз безопасности информации`;
	}, [toggle]);

	useEffect(() => {
		setCurrentObject(active || initial);
	}, [active]);

	useEffect(() => {
		directoryAPI.violatorLevelsList({ id: Number(params.threadId), type: "THREAT" }).then((r) => {
			setOptions(r.data);
		});
		return () => setOptions([]);
		//eslint-disable-next-line
	}, [toggle]);

	const save = () => {
		const { id, level, name, ...rest } = currentObject;
		if (toggle === "edit") {
			organizationAPI.objectLevelUpdate(id!, rest).then((r) => {
				const response = r.data;
				const newData = [...data].map((_) => (_.id === response.id ? response : _));
				replace(newData);
				setCurrentObject(initial);
				snackWrapper("success", "Таблица обновлена");
				updateTabs("LEVEL");
				close();
			});
		} else if (toggle === "create") {
			organizationAPI.objectLevelCreate(+params.threadId!, { ...rest, level, name }).then((r) => {
				replace(r.data);
				setCurrentObject(initial);
				snackWrapper("success", "Таблица обновлена");
				updateTabs("LEVEL");
				close();
			});
		}
	};
	const disabled = useMemo(() => {
		return !currentObject.level || !currentObject.type;
	}, [currentObject]);
	return toggle !== null ? (
		<ModalPortal outSide={false} size={"md"} title={title} handleClose={close}>
			<div className={sass.modal}>
				{toggle === "create" ? (
					<InputTemp
						options={options as []}
						name={"level"}
						onSelect={(e: ViolatorLevelResponse) => {
							setCurrentObject((prevState) => ({
								...prevState,
								level: e.level || "",
								type: e.type || "",
								possibility: e.opportunities,
								name: e.name,
							}));
						}}
						type={"select"}
						readOnly={true}
						value={currentObject.level}
						placeholder={"Введите текст"}
						field={["level", "name"]}
						label={"Уровень возможностей нарушителей"}
						required={true}
					/>
				) : (
					<TextArea
						name={"level"}
						readOnly
						value={currentObject.level}
						placeholder={"Введите текст"}
						label={"Уровень возможностей нарушителей"}
						required={true}
					/>
				)}
				<TextArea
					name={"possibility"}
					onChange={changeField}
					value={currentObject.possibility || ""}
					placeholder={"Введите текст"}
					label={"Возможности нарушителей по реализации угроз безопасности информации"}
					rows={10}
				/>
				<TextArea
					name={"type"}
					onChange={changeField}
					value={currentObject.type}
					placeholder={"Введите комментарий"}
					label={"Виды нарушителей"}
					required={true}
					rows={5}
				/>
				<div className={sass.btn}>
					<Button onClick={close} variant={BUTTON_VARIANTS.OUTLINED}>
						Отменить
					</Button>
					<Button disabled={disabled} onClick={save} variant={BUTTON_VARIANTS.CONTAINED}>
						Сохранить
					</Button>
				</div>
			</div>
		</ModalPortal>
	) : null;
};

export default LevelsOfVariablesModule;
